import confetti from 'canvas-confetti';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

export function removeIdFromArray(ids, idToRemove) {
  return [...ids.filter((id) => id !== idToRemove)];
}

export function getAnswersForQuestion(
  questionToGetAnswersFor,
  submittedQuestionAnswers,
) {
  return submittedQuestionAnswers?.filter(
    (answer) =>
      answer.questionId.toString() === questionToGetAnswersFor.id.toString(),
  )?.[0];
}

export function getCorrectAnswersForQuestion(answers) {
  return answers?.questionAnswersMultipleChoice
    ?.filter((answer) => answer.correct)
    .map((answer) => answer.id);
}

export function getUserAnswers(answers) {
  return answers?.userAnswers;
}

export function showSuccessAnimationIfAllIsCorrect(
  questions,
  answers,
  canOverrideAnswers,
) {
  const allCorrectItemIds = questions.questionAnswersMultipleChoice
    .filter((i) => i.correct)
    .map((i) => i.id.toString());

  const allChosenItemIds = answers.map((a) => a.toString());

  const allChosenWrongItemIds = allChosenItemIds.filter(
    (id) => !allCorrectItemIds.includes(id),
  );

  // user has chosen one or more wrong items
  if (allChosenWrongItemIds.length > 0) {
    return;
  }

  const allChosenCorrectItemIds = allChosenItemIds.filter((id) =>
    allCorrectItemIds.includes(id),
  );

  if (allChosenCorrectItemIds.length === allCorrectItemIds.length) {
    if (!canOverrideAnswers) {
      confetti({
        gravity: 2,
        origin: { y: 0.6 },
        particleCount: 100,
        spread: 70,
        startVelocity: 60,
        ticks: 70,
      });
    }
  }
}

export function getAnswerCopy(answer, answersForThisQuestion, answerId) {
  const answerCopy = { ...answer };

  if (answersForThisQuestion?.length) {
    const fullResult = answersForThisQuestion.filter(
      (answer) => answer.id === answerId,
    )?.[0];
    answerCopy.correct = Boolean(fullResult?.correct);
    answerCopy.explanation = fullResult?.explanation;
  }

  return answerCopy;
}

/**
 * A helper funtion to get the string respresentation for a multipleChoiceAnswerCard at a given index
 *
 * @param {number} index
 * @returns {string} The string representation of the index
 */
const indexToUniqueString = (index) => {
  const numberOfLettersInAlphabet = 1 + 'Z'.charCodeAt(0) - 'A'.charCodeAt(0);
  return String.fromCharCode(
    (index % numberOfLettersInAlphabet) + 'A'.charCodeAt(0),
  ).repeat(1 + index / numberOfLettersInAlphabet);
};

export function getIconForMultipleChoiceAnswerCard(answer, hasResults, index) {
  let resultIcon = hasResults ? (
    <CheckIcon className="w-8 h-8" />
  ) : (
    indexToUniqueString(index)
  );
  if (hasResults && !answer.correct)
    resultIcon = <XMarkIcon className="w-8 h-8" />;

  return resultIcon;
}

export function getButtonText(canOverrideAnswers, canSubmit, t) {
  let buttonText =
    canOverrideAnswers && canSubmit
      ? t('styled-slide-viewer.save')
      : t('styled-slide-viewer.saved');
  if (!canOverrideAnswers)
    buttonText = canSubmit
      ? t('styled-slide-viewer.submit')
      : t('styled-slide-viewer.submitted');

  return buttonText;
}

export function doesUserHaveResults({
  canOverrideAnswers,
  canSubmit,
  hasSelectedAnAnswerAsTeacher,
  selectedPeriodIsNotActivePeriod,
  showAnswers,
  showAnswersInSidebar,
  userAnswers,
}) {
  // the below is not technically true as canSubmit is always true when called from the ck5slideviewer and
  // always false when called from elsewhere.
  const hasSubmittedAndCantBeOverridden = !canSubmit && !canOverrideAnswers;
  // noinspection UnnecessaryLocalVariableJS
  const hasResults =
    // when the user has submitted answers, and it can't be overridden
    hasSubmittedAndCantBeOverridden ||
    // or when the teacher hit submit
    hasSelectedAnAnswerAsTeacher ||
    // or when an answer was entered
    Boolean(userAnswers?.length) ||
    // or when it's a period in the past
    selectedPeriodIsNotActivePeriod ||
    // or when it's a teacher in the sideBar
    showAnswersInSidebar ||
    // or when it's a teacher in presentation mode
    showAnswers;

  return hasResults;
}

// Merge new answers with existing answers, preserving all properties
export function mergeAnswers(answers, newAnswers) {
  return answers.map((answer) => {
    const serverAnswer = newAnswers.find(
      (sa) => sa.id === parseInt(answer.id, 10),
    );
    if (serverAnswer) {
      // Merge server answer with existing answer, server properties take precedence
      return {...answer, ...serverAnswer};
    }
    return answer;
  });
}
